import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { HEARTBEATS_PER_MINUTE } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import ProgressBar from '../ProgressBar';
import FunctionHealthCTA from '../FunctionHealthCTA';
import { springConfig } from '../../utils/animations';

interface HeartbeatsViewProps {
  stats: LifeStats;
}

const HeartbeatsView: React.FC<HeartbeatsViewProps> = ({ stats }) => {
  const prefersReducedMotion = useReducedMotion();
  const [beatCount, setBeatCount] = useState(0);

  // Simulate real-time heartbeat counter
  useEffect(() => {
    if (prefersReducedMotion) return;

    const beatsPerSecond = HEARTBEATS_PER_MINUTE / 60;
    const interval = setInterval(() => {
      setBeatCount(prev => prev + 1);
    }, 1000 / beatsPerSecond);

    return () => clearInterval(interval);
  }, [prefersReducedMotion]);

  if (stats.totalHeartbeats <= 0) return null;

  // Calculate display values
  const heartbeatsBillions = (stats.heartbeatsLived / 1000000000).toFixed(2);
  const totalHeartbeatsBillions = (stats.totalHeartbeats / 1000000000).toFixed(2);
  const heartbeatDays = Math.floor(stats.heartbeatsLived / (24 * 60 * HEARTBEATS_PER_MINUTE));

  // Calculate beat duration in ms (60000ms / BPM)
  const beatDuration = 60000 / HEARTBEATS_PER_MINUTE / 1000; // in seconds

  // Heartbeat animation variants - synced to BPM
  const heartbeatVariants = {
    beat: {
      scale: [1, 1.2, 1, 1.15, 1],
      transition: {
        duration: beatDuration,
        repeat: Infinity,
        ease: 'easeInOut' as const,
        times: [0, 0.15, 0.3, 0.45, 0.6]
      }
    }
  };

  // Pulse ring animation
  const pulseRingVariants = {
    animate: {
      scale: [1, 1.5, 2],
      opacity: [0.6, 0.3, 0],
      transition: {
        duration: beatDuration * 2,
        repeat: Infinity,
        ease: 'easeOut' as const
      }
    }
  };

  // Stats card animation
  const statCardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        ...springConfig.gentle,
        delay: i * 0.1
      }
    }),
    hover: {
      scale: 1.05,
      y: -4,
      transition: springConfig.snappy
    }
  };

  // Format large number with suffix
  const formatNumber = (num: number): string => {
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(2)}B`;
    } else if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <div className="w-full max-w-4xl mx-auto text-center">
        <Card className="p-6" animated={false}>
          <div className="flex items-center gap-3 mb-6 justify-center">
            <Heart size={24} className="text-red-500" fill="#ef4444" />
            <h2 className="text-2xl font-bold dark:text-catppuccin-text">Your Heart Has Beaten</h2>
          </div>

          <div className="flex flex-col items-center justify-center mb-8">
            <Heart size={120} className="text-red-500 mx-auto mb-6" fill="#ef4444" />
            <p className="text-5xl font-bold text-red-500 mb-4">
              {stats.heartbeatsLived.toLocaleString()}
            </p>
            <p className="text-gray-600 dark:text-catppuccin-subtext0 mb-6">
              Out of approximately {totalHeartbeatsBillions} billion heartbeats in a lifetime
            </p>
          </div>

          <ProgressBar
            value={stats.heartbeatsLived}
            maxValue={stats.totalHeartbeats}
            colorClass="bg-red-500"
            animate={false}
          />

          <div className="flex flex-wrap justify-center gap-6 mt-10">
            <div className="bg-red-50 dark:bg-catppuccin-surface1 p-4 rounded-lg w-52">
              <h3 className="text-sm font-semibold text-red-700 dark:text-catppuccin-red mb-1">Average Per Day</h3>
              <p className="text-2xl font-bold text-red-800 dark:text-catppuccin-text">
                {(HEARTBEATS_PER_MINUTE * 60 * 24).toLocaleString()}
              </p>
            </div>
            <div className="bg-pink-50 dark:bg-catppuccin-surface1 p-4 rounded-lg w-52">
              <h3 className="text-sm font-semibold text-pink-700 dark:text-catppuccin-pink mb-1">Heartbeat Days</h3>
              <p className="text-2xl font-bold text-pink-800 dark:text-catppuccin-text">
                {heartbeatDays.toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-catppuccin-surface1 p-4 rounded-lg w-52">
              <h3 className="text-sm font-semibold text-purple-700 dark:text-catppuccin-mauve mb-1">Average Rate</h3>
              <p className="text-2xl font-bold text-purple-800 dark:text-catppuccin-text">
                {HEARTBEATS_PER_MINUTE} BPM
              </p>
            </div>
          </div>

          <FunctionHealthCTA />
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto text-center">
      <Card className="p-6" animated={true}>
        <motion.div
          className="flex items-center gap-3 mb-6 justify-center"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: beatDuration, repeat: Infinity }}
          >
            <Heart size={24} className="text-red-500" fill="#ef4444" />
          </motion.div>
          <h2 className="text-2xl font-bold dark:text-catppuccin-text">Your Heart Has Beaten</h2>
        </motion.div>

        {/* Large animated heart */}
        <div className="flex flex-col items-center justify-center mb-8">
          <div className="relative mb-10">
            {/* Pulse rings */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              variants={pulseRingVariants}
              animate="animate"
            >
              <div className="w-40 h-40 rounded-full border-2 border-red-300" />
            </motion.div>
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              variants={pulseRingVariants}
              animate="animate"
              style={{ animationDelay: `${beatDuration}s` }}
            >
              <div className="w-40 h-40 rounded-full border-2 border-red-200" />
            </motion.div>

            {/* Main heart */}
            <motion.div
              variants={heartbeatVariants}
              animate="beat"
              className="relative z-10"
            >
              <Heart
                size={160}
                className="text-red-500 mx-auto drop-shadow-lg"
                fill="#ef4444"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <motion.span
                    className="text-white font-bold text-2xl drop-shadow-md"
                    key={beatCount}
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.1 }}
                  >
                    {heartbeatsBillions}B
                  </motion.span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Heartbeat line graph */}
          <motion.div
            className="w-full max-w-lg h-20 relative mb-8 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <svg className="w-full h-full" viewBox="0 0 400 80" preserveAspectRatio="none">
              <motion.path
                d="M0,40 L30,40 L40,10 L50,70 L60,40 L90,40 L100,40 L110,10 L120,70 L130,40 L160,40 L170,40 L180,10 L190,70 L200,40 L230,40 L240,40 L250,10 L260,70 L270,40 L300,40 L310,40 L320,10 L330,70 L340,40 L370,40 L380,40 L390,40 L400,40"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: 1,
                  opacity: 1,
                  x: [0, -120, 0]
                }}
                transition={{
                  pathLength: { duration: 1, delay: 0.3 },
                  opacity: { duration: 0.5 },
                  x: { duration: 6, repeat: Infinity, ease: 'linear' }
                }}
              />
            </svg>
          </motion.div>
        </div>

        <div className="my-8">
          <motion.p
            className="text-5xl font-bold text-red-500 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...springConfig.bouncy, delay: 0.4 }}
          >
            {formatNumber(stats.heartbeatsLived)}
          </motion.p>
          <motion.p
            className="text-gray-600 dark:text-catppuccin-subtext0 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Out of approximately {totalHeartbeatsBillions} billion heartbeats in a lifetime
          </motion.p>

          <ProgressBar
            value={stats.heartbeatsLived}
            maxValue={stats.totalHeartbeats}
            colorClass="bg-red-500"
            animate={true}
          />
        </div>

        <motion.div
          className="flex flex-wrap justify-center gap-6 mt-10"
          initial="hidden"
          animate="visible"
        >
          {[
            {
              bg: 'bg-red-50 dark:bg-catppuccin-surface1',
              labelColor: 'text-red-700 dark:text-catppuccin-red',
              valueColor: 'text-red-800 dark:text-catppuccin-text',
              label: 'Average Per Day',
              value: (HEARTBEATS_PER_MINUTE * 60 * 24).toLocaleString()
            },
            {
              bg: 'bg-pink-50 dark:bg-catppuccin-surface1',
              labelColor: 'text-pink-700 dark:text-catppuccin-pink',
              valueColor: 'text-pink-800 dark:text-catppuccin-text',
              label: 'Heartbeat Days',
              value: heartbeatDays.toLocaleString()
            },
            {
              bg: 'bg-purple-50 dark:bg-catppuccin-surface1',
              labelColor: 'text-purple-700 dark:text-catppuccin-mauve',
              valueColor: 'text-purple-800 dark:text-catppuccin-text',
              label: 'Average Rate',
              value: `${HEARTBEATS_PER_MINUTE} BPM`
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              className={`${stat.bg} p-4 rounded-lg w-52`}
              variants={statCardVariants}
              custom={index}
              whileHover="hover"
            >
              <h3 className={`text-sm font-semibold ${stat.labelColor} mb-1`}>{stat.label}</h3>
              <p className={`text-2xl font-bold ${stat.valueColor}`}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="mt-8 text-gray-500 dark:text-catppuccin-subtext0 text-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <p>Based on an average heart rate of {HEARTBEATS_PER_MINUTE} beats per minute.</p>
          <p>Your actual heart rate may vary.</p>
        </motion.div>

        <FunctionHealthCTA />
      </Card>
    </div>
  );
};

export default HeartbeatsView;
